import type { Option } from "@tento-lona/sheets";
import "@tento-ui/components/core/avatar-editable";
import "./context-menu/rsvp-status-list.ts";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Anchor, AnchorInstance } from "@tento-ui/components/anchor";
import { AvatarEditable } from "@tento-ui/components/core/avatar-editable";
import { Round } from "@tento-ui/components/round";
import type {
  AuthScopedCalendarDisplay,
  AuthScopedCalendarId,
} from "@tento-lona";
import { type EventCategories, EventCategoryItem, type EventsClient } from "@tento-lona";
import { CalendarPicker } from "./calendar-picker.ts";
import { EventTagList } from "./event-context-menu.ts";
import { RsvpStatusList } from "./context-menu/rsvp-status-list.ts";
import { LonaIcons, TEMP_CALENDAR_ICON } from "@tento-ui/ui/icons";
import { Network } from "@tento-lona/network";
import { css, Style } from "@tento-ui/component-styles";

@component({
  name: "lona-event-icon",
})
export class EventIcon extends LonaWebComponent {
  $icon: AvatarEditable = this.hydrate("icon");

  clistShown = false;
  tagListShown = false;

  category: Option<EventCategoryItem> = null;
  selectedCal: Option<AuthScopedCalendarId> = null;

  #delegate: Option<{
    /** The client's event categories (`client.events.categories`) the tag list offers. */
    categories: EventCategories;
    onSelectCategory: (category: Option<EventCategoryItem>) => void;
    onSelectCalendar: (acal: AuthScopedCalendarDisplay) => void;
    getCalendars: () => readonly Network.CalendarList[];
    calendarWritability?: (calendar: AuthScopedCalendarDisplay) => Option<EventsClient.Writability>;
  }> = null;

  bindDelegate(delegate: {
    categories: EventCategories;
    onSelectCategory: (category: Option<EventCategoryItem>) => void;
    onSelectCalendar: (acal: AuthScopedCalendarDisplay) => void;
    getCalendars: () => readonly Network.CalendarList[];
    /** Whether a calendar takes writes, as the client knows it: the picker leaves out one it knows is read-only. */
    calendarWritability?: (calendar: AuthScopedCalendarDisplay) => Option<EventsClient.Writability>;
  }): this {
    this.#delegate = delegate;
    return this;
  }

  onClickBadge() {
    this.clistShown = true;
    this.$icon.toggleAttribute("active-badge", true);
    this.$icon.toggleAttribute("active-avatar", this.tagListShown);
    EventIcon.showCalendarList({
      $anchor: this.$icon,
      ucals: this.#delegate?.getCalendars() ?? [],
      selected: this.selectedCal,
      writability: this.#delegate?.calendarWritability ?? null,
      onSelectCalendar: (acal) => {
        this.#delegate?.onSelectCalendar(acal);
        this.rebind();
      },
      context: "event-details",
      cleanup: () => {
        this.clistShown = false;
        this.$icon.toggleAttribute("active-badge", this.clistShown);
        this.$icon.toggleAttribute("active-avatar", this.tagListShown);
      },
    });
  }

  onClickContent() {
    const delegate = this.#delegate;
    if (delegate == null) return;
    this.tagListShown = true;
    this.$icon.toggleAttribute("active-avatar", true);
    this.$icon.toggleAttribute("active-badge", this.clistShown);
    EventIcon.showTagList(
      this.$icon,
      delegate.categories,
      (category) => {
        delegate.onSelectCategory(category);
        this.rebind();
      },
      "event-details",
      () => {
        this.tagListShown = false;
        this.$icon.toggleAttribute("active-avatar", this.tagListShown);
        this.$icon.toggleAttribute("active-badge", this.clistShown);
      },
    );
  }

  bind(props: EventIcon.Props) {
    this.clistShown = false;
    this.tagListShown = false;
    this.category = props.category;
    this.selectedCal = props.selectedCal;
    this.rebind();
  }

  rebind() {
    this.$icon.bind({
      onClickBadge: this.onClickBadge.bind(this),
      onClickContent: this.onClickContent.bind(this),
      content: this.category?.emoji ?? "📅",
      contentBg: this.category == null ? "var(--gray-dimmed)" : this.category.colorOpt() ?? "var(--gray)",
      badgeBg: "",
      badgeContent: LonaIcons.make(TEMP_CALENDAR_ICON),
    });
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        z-index: 1;
        position: relative;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-editable-avatar id=icon></std-editable-avatar>
  `;
}

export namespace EventIcon {
  export type Props = {
    category: Option<EventCategoryItem>;
    selectedCal: Option<AuthScopedCalendarId>;
  };

  export function showTagList(
    $anchor: HTMLElement,
    categories: EventCategories,
    onSelectTag: (tag: EventCategoryItem) => void,
    context: string,
    cleanup: () => void,
  ) {
    let handle: Option<AnchorInstance>;

    const $tagList = EventTagList.makeWith(categories, (tag) => {
      onSelectTag?.(tag);
      handle?.dismiss();
    });

    // Wrap in Round container for proper sizing (like showCalendarList)
    const $container = Round.makeWithContent($tagList, {
      bg: "var(--bg-elevated)",
      radius: 8,
      shadow: "var(--box-shadow)",
    }).assignStyles({
      height: "min-content",
      padding: "8px",
      width: "200px",
    });
    $container.setAttribute("context", context);

    // Custom position: bottom-left of anchor with 8px spacing
    const customPosition: Anchor.PositionStrategy = () => {
      const anchorRect = $anchor.getBoundingClientRect();
      const parentRect = (
        $anchor.parentElement ?? $anchor
      ).getBoundingClientRect();

      return {
        top: anchorRect.bottom - parentRect.top + 8 + "px",
        left: anchorRect.left - parentRect.left + "px",
      };
    };

    handle = Anchor.reveal({
      $anchor: $anchor,
      $content: $container,
      position: customPosition,
      escapeContainer: false,
      onDismiss: cleanup,
    });
  }

  export function showCalendarList(props: {
    $anchor: HTMLElement;
    ucals: readonly Network.CalendarList[];
    selected: Option<AuthScopedCalendarId>;
    writability: Option<(calendar: AuthScopedCalendarDisplay) => Option<EventsClient.Writability>>;
    onSelectCalendar: (calendar: AuthScopedCalendarDisplay) => void;
    context: string;
    cleanup: () => void;
  }) {
    let handle: Option<AnchorInstance>;

    const $picker = CalendarPicker.makeWith({
      ucals: props.ucals,
      ...(props.selected != null ? { selected: props.selected } : {}),
      onSelect: (acal) => {
        props.onSelectCalendar?.(acal);
        handle?.dismiss();
      },
      showEditableOnly: true,
      ...(props.writability != null ? { writability: props.writability } : {}),
    }).assignStyles({
      width: "100%",
    });
    $picker.focus();

    const $container = Round.makeWithContent($picker, {
      bg: "var(--bg-elevated)",
      radius: 8,
      shadow: "var(--box-shadow)",
    }).assignStyles({
      height: "min-content",
      padding: "8px",
      width: "308px",
    });
    $container.setAttribute("context", props.context);

    // Custom position: bottom-left of anchor with 8px spacing
    // For absolute positioning, compute position relative to anchor's parent
    const customPosition: Anchor.PositionStrategy = () => {
      const anchorRect = props.$anchor.getBoundingClientRect();
      const parentRect = (
        props.$anchor.parentElement ?? props.$anchor
      ).getBoundingClientRect();

      return {
        top: anchorRect.bottom - parentRect.top + 8 + "px",
        left: anchorRect.left - parentRect.left + "px",
      };
    };

    handle = Anchor.reveal({
      $anchor: props.$anchor,
      $content: $container,
      position: customPosition,
      escapeContainer: false,
      onDismiss: props.cleanup,
    });
  }

  export function showRsvpStatusList(
    $anchor: HTMLElement,
    onSelectStatus: (status: string) => void,
    context: string,
  ) {
    let handle: Option<AnchorInstance>;

    const $statusList = RsvpStatusList.makeWith((status) => {
      onSelectStatus?.(status);
      handle?.dismiss();
    }).assignStyles({
      maxWidth: "400px",
      overflowY: "auto",
      width: "100%",
    });

    const $container = Round.makeWithContent($statusList, {
      bg: "var(--bg-elevated)",
      radius: 8,
      shadow: "var(--box-shadow)",
    }).assignStyles({
      height: "max-content",
    });
    $container.setAttribute("context", context);

    // Custom position: bottom-left of anchor with 4px spacing
    // For absolute positioning, compute position relative to anchor's parent
    const customPosition: Anchor.PositionStrategy = () => {
      const anchorRect = $anchor.getBoundingClientRect();
      const parentRect = (
        $anchor.parentElement ?? $anchor
      ).getBoundingClientRect();

      return {
        top: anchorRect.bottom - parentRect.top + 4 + "px",
        left: anchorRect.left - parentRect.left + "px",
      };
    };

    $container.assignStyles({ width: "240px" });
    handle = Anchor.reveal({
      $anchor: $anchor,
      $content: $container,
      position: customPosition,
      escapeContainer: false,
    });
  }
}
